/**
 * Route gate that enforces the two RBAC rules called out in S1-011's
 * acceptance criteria:
 *
 *   - Unauthenticated user is redirected to `/login`.
 *   - Student / admin / support attempting `/teacher/*` gets a 403 page.
 *
 * The 403 case is intentionally stricter than the worker-side RBAC
 * (admin/support can read teacher data on the API), because the *UI*
 * route segregation in v1.2 of the design doc is per-persona. Admins
 * have their own `/admin/*` shell, students have `/student/*`. This is
 * the path Sprint 1 ships; later sprints will fill the other shells.
 */

import { Navigate, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useSession } from './SessionProvider';
import type { UiRole } from '../api/types';
import { Forbidden } from '../routes/Forbidden';

export interface RequireRoleProps {
  allowed: ReadonlyArray<UiRole>;
  children: ReactNode;
}

export function RequireRole({ allowed, children }: RequireRoleProps) {
  const { session } = useSession();
  const location = useLocation();

  if (!session) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  if (!allowed.includes(session.principal.role)) {
    return <Forbidden role={session.principal.role} />;
  }
  return <>{children}</>;
}
